import React, { useState, useEffect } from 'react';
import CustomerField from '../CustomerField';
import { toast } from 'react-toastify';

const generateTimes = () => {
  const times = [];
  for (let h = 9; h <= 21; h++) {
    times.push(`${String(h).padStart(2, '0')}:00`);
    times.push(`${String(h).padStart(2, '0')}:30`);
  }
  return times;
};

const AppointmentModal = ({
  isOpen,
  onClose,
  onSaved,
  slotInfo,
  employee,
  appointment = null,
  appointments = [],
  setSelectedAppointment,
}) => {
  const [customer, setCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [timeError, setTimeError] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [status, setStatus] = useState(''); // ✅ Νέο state

  useEffect(() => {
    if (!startTime || !endTime) {
      setTimeError('');
      return;
    }
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (end <= start) {
      setTimeError('❌ Η ώρα λήξης πρέπει να είναι μετά την ώρα έναρξης.');
    } else {
      setTimeError('');
    }
  }, [startTime, endTime]);

  useEffect(() => {
    if (!isOpen) return;

    setServiceId('');
    setStartTime('');
    setEndTime('');
    setExistingAppointments([]);
    setStatus(''); // ✅ reset

    // Φόρτωση υπηρεσιών
    fetch('https://api.remindly.gr/get-services.php', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const activeServices = data.filter(s => s.active === 1 || s.active === '1');
          setServices(activeServices);
        } else {
          setServices([]);
        }
      });

    const baseStart = appointment ? new Date(appointment.start) : new Date(slotInfo?.start);
    const baseEnd = appointment ? new Date(appointment.end) : new Date(baseStart.getTime() + 30 * 60000);

    const baseStartStr = `${String(baseStart.getHours()).padStart(2, '0')}:${String(baseStart.getMinutes()).padStart(2, '0')}`;
    const baseEndStr = `${String(baseEnd.getHours()).padStart(2, '0')}:${String(baseEnd.getMinutes()).padStart(2, '0')}`;

    setStartTime(baseStartStr);
    setEndTime(baseEndStr);

    if (appointment) {
      setCustomer({ id: appointment.customer_id, name: appointment.customer });
      setServiceId(appointment.service_id || '');
      setSendSms(Boolean(appointment.send_sms));
      setSendEmail(Boolean(appointment.send_email));
      setStatus(appointment.status || ''); // ✅ φόρτωση υπάρχουσας κατάστασης
    } else {
      setCustomer(null);
      setSendSms(false);
      setSendEmail(false);
    }

    // Φόρτωση υπαρχόντων ραντεβού
    if (employee && baseStart) {
      const date = baseStart.toISOString().slice(0, 10);
      fetch(`https://api.remindly.gr/get-appointments.php?employee_id=${employee.id}&date=${date}`)
        .then(res => res.json())
        .then(data => {
          const filtered = data.filter(a => String(a.id) !== String(appointment?.id));
          setExistingAppointments(filtered);
        });
    }
  }, [isOpen, slotInfo, employee, appointment]);

  const formatDateTime = (baseDate, timeStr) => {
    const [h, m] = timeStr.split(':');
    const d = new Date(baseDate);
    d.setHours(Number(h));
    d.setMinutes(Number(m));
    d.setSeconds(0);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  };

  const handleSave = () => {
    if (!customer || !serviceId || !startTime || !endTime) {
      toast.error('Συμπλήρωσε όλα τα πεδία.');
      return;
    }

    const baseDate = appointment ? new Date(appointment.start) : new Date(slotInfo.start);
    const newStart = formatDateTime(baseDate, startTime);
    const newEnd = formatDateTime(baseDate, endTime);

    const url = appointment
      ? 'https://api.remindly.gr/update-appointment.php'
      : 'https://api.remindly.gr/add-appointment.php';

    const payload = {
      employee_id: employee.id,
      customer_id: customer.id,
      service_id: serviceId,
      start_time: newStart,
      end_time: newEnd,
      wants_sms: sendSms ? 1 : 0,
      wants_email: sendEmail ? 1 : 0,
      status: status || null, // ✅ στέλνεται μόνο αν υπάρχει
    };

    if (appointment) payload.id = appointment.id;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        console.log('📦 Response from update-appointment.php:', data); // 👈
        if (data.success) {
          toast.success(`Το Ραντεβού ${appointment ? 'ενημερώθηκε' : 'καταχωρήθηκε'}!`);
          onSaved();
          onClose();
        } else {
          alert('❌ Σφάλμα κατά την αποθήκευση');
        }
      });

    fetch('https://api.remindly.gr/update-customer-preferences.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: customer.id,
        wants_sms: sendSms,
        wants_email: sendEmail,
      }),
    });
  };

  const handleDelete = () => {
    if (!appointment) return;
    if (!window.confirm('❗ Θες σίγουρα να διαγράψεις το ραντεβού;')) return;

    fetch('https://api.remindly.gr/delete-appointment.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: appointment.id }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          toast.success('Το Ραντεβού διαγράφηκε.');
          onSaved();
          onClose();
        } else {
          alert('❌ Σφάλμα κατά τη διαγραφή');
        }
      });
  };

  if (!isOpen) return null;
  const hours = generateTimes();

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
      <div className="relative bg-white p-6 rounded-lg w-full max-w-lg shadow-xl">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          {appointment
            ? '✏️ Επεξεργασία Ραντεβού'
            : `➕ Νέο Ραντεβού ${employee ? `για ${employee.name}` : ''}`}
        </h2>

        {/* Ώρες */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ώρα Έναρξης</label>
            <input
              type="search"
              list="hours"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full border p-2 rounded text-sm"
              placeholder="π.χ. 13:30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ώρα Λήξης</label>
            <input
              type="search"
              list="hours"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full border p-2 rounded text-sm"
              placeholder="π.χ. 14:00"
            />
          </div>
        </div>

        {timeError && <div className="text-red-600 text-sm mb-2">{timeError}</div>}
        <datalist id="hours">{hours.map((t) => <option key={t} value={t} />)}</datalist>

        {/* Πελάτης */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Πελάτης</label>
          <CustomerField value={customer} onSelect={setCustomer} />
        </div>

        {/* Υπηρεσία */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Υπηρεσία</label>
          <select
            className="w-full border p-2 rounded"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            <option value="">Επέλεξε υπηρεσία</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* ✅ ΝΕΟ ΠΕΔΙΟ: Κατάσταση */}
{appointment && (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">Κατάσταση</label>
    <select
      className="w-full border p-2 rounded"
      value={status}
      onChange={(e) => setStatus(e.target.value)}
    >
      <option value="">✅ Ολοκληρωμένο</option>
      <option value="cancelled">❌ Ακυρώθηκε</option>
      <option value="no_show">🚫 Δεν ήρθε</option>
    </select>
  </div>
)}


        {/* Κουμπιά */}
        <div className="flex justify-between items-center mt-6">
          {appointment && (
            <button
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
              onClick={handleDelete}
            >
              Διαγραφή
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded"
              onClick={onClose}
            >
              Άκυρο
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              onClick={handleSave}
            >
              {appointment ? 'Αποθήκευση' : 'Καταχώριση'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;
